// The Wilds map: a 40 x 30 tile meadow with woods, a pond, paths, and one landmark door per
// fun game. The layout is built programmatically so it always stays rectangular and the paths
// to every door are carved, not hoped for.

export const COLS = 40;
export const ROWS = 30;
export const TILE = 24;

export const TILE_ID = { GRASS: 0, PATH: 1, TREE: 2, WATER: 3, ROCK: 4, FLOWER: 5, FENCE: 6, START: 7, WILD: 8, DOOR0: 10 } as const;
export const SOLID_IDS = [TILE_ID.TREE, TILE_ID.WATER, TILE_ID.ROCK, TILE_ID.FENCE];
export const MAX_DOORS = 6;

export const LEGEND: Record<string, number> = {
  '.': TILE_ID.GRASS, '=': TILE_ID.PATH, 'T': TILE_ID.TREE, '~': TILE_ID.WATER, 'r': TILE_ID.ROCK,
  'f': TILE_ID.FLOWER, '#': TILE_ID.FENCE, 'S': TILE_ID.START, 'W': TILE_ID.WILD,
  '1': TILE_ID.DOOR0, '2': TILE_ID.DOOR0 + 1, '3': TILE_ID.DOOR0 + 2, '4': TILE_ID.DOOR0 + 3, '5': TILE_ID.DOOR0 + 4, '6': TILE_ID.DOOR0 + 5,
};

/** door slots in the order fun games are assigned to them (registry order) */
export const DOOR_SLOTS: { tx: number; ty: number }[] = [
  { tx: 6, ty: 5 },    // NW woods: a cave
  { tx: 34, ty: 4 },   // NE: a crag
  { tx: 8, ty: 24 },   // SW meadow: a tent
  { tx: 33, ty: 24 },  // SE: a table
  { tx: 20, ty: 3 },   // N: a flag run
  { tx: 31, ty: 15 },  // E: an arena
];
export const START = { tx: 20, ty: 16 };
export const WILD_FLOWERS: { tx: number; ty: number }[] = [
  { tx: 3, ty: 13 }, { tx: 14, ty: 27 }, { tx: 37, ty: 10 }, { tx: 25, ty: 8 }, { tx: 36, ty: 27 },
];

export type LandmarkKind = 'cave' | 'crag' | 'tent' | 'table' | 'flagrun' | 'arena' | 'signpost';
/** landmark art per game id; unknown ids fall back to a signpost so new games just appear */
export const LANDMARKS: Record<string, { kind: LandmarkKind; label: string }> = {
  'fun.haunted-house': { kind: 'cave', label: 'Haunted House' },
  'fun.dragon-keep': { kind: 'crag', label: 'Dragon Keep' },
  'fun.where-is-it': { kind: 'tent', label: 'Where Is It' },
  'fun.snap': { kind: 'table', label: 'Snap' },
  'fun.gem-run': { kind: 'flagrun', label: 'Gem Run' },
  'fun.pet-arena': { kind: 'arena', label: 'Pet Arena' },
};

function buildRows(): string[] {
  const g: string[][] = Array.from({ length: ROWS }, () => Array.from({ length: COLS }, () => '.'));
  const set = (x: number, y: number, ch: string) => { if (x >= 0 && y >= 0 && x < COLS && y < ROWS) g[y][x] = ch; };
  const rect = (x0: number, y0: number, x1: number, y1: number, ch: string) => { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) set(x, y, ch); };
  const blob = (cx: number, cy: number, rx: number, ry: number, ch: string) => { for (let y = cy - ry; y <= cy + ry; y++) for (let x = cx - rx; x <= cx + rx; x++) { const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2; if (d <= 1) set(x, y, ch); } };

  // border
  rect(0, 0, COLS - 1, 0, 'T'); rect(0, ROWS - 1, COLS - 1, ROWS - 1, 'T'); rect(0, 0, 0, ROWS - 1, 'T'); rect(COLS - 1, 0, COLS - 1, ROWS - 1, 'T');
  // woods
  blob(6, 5, 5, 4, 'T');        // NW woods around the cave
  blob(13, 9, 3, 2, 'T');
  blob(34, 4, 4, 3, 'r');       // NE crag rocks
  blob(27, 21, 3, 2, 'T');
  blob(4, 20, 2, 3, 'T');
  rect(16, 25, 23, 27, 'T');    // south thicket
  // pond
  blob(30, 9, 4, 3, '~');
  // scattered rocks and flowers
  for (const [x, y] of [[11, 18], [24, 13], [36, 19], [9, 12], [26, 5], [15, 22]]) set(x, y, 'r');
  for (const [x, y] of [[3, 3], [10, 15], [17, 7], [22, 20], [28, 26], [37, 14], [12, 28], [2, 26], [23, 10], [35, 22], [7, 17], [19, 23]]) set(x, y, 'f');
  // fence with a gap
  rect(4, 22, 12, 22, '#'); set(8, 22, '.');
  // main paths
  rect(2, 15, 37, 15, '=');
  rect(20, 2, 20, 27, '=');
  // carve a path to each door (L-shaped from the nearest main path)
  for (const d of DOOR_SLOTS) {
    const ty = d.ty, tx = d.tx;
    // vertical from the horizontal path at y=15 up/down to ty, then horizontal to tx
    const x0 = Math.abs(tx - 20) < 6 ? 20 : tx;
    if (x0 === 20) { // use the vertical main path, then go sideways
      const yFrom = ty; for (let x = Math.min(20, tx); x <= Math.max(20, tx); x++) set(x, yFrom, '=');
    } else {
      for (let y = Math.min(15, ty); y <= Math.max(15, ty); y++) set(tx, y, '=');
    }
  }
  // doors on top of the carved paths
  DOOR_SLOTS.forEach((d, i) => set(d.tx, d.ty, String(i + 1)));
  // wild flowers (walkable pickups) and the start
  for (const w of WILD_FLOWERS) set(w.tx, w.ty, 'W');
  set(START.tx, START.ty, 'S');
  // make sure every door and flower has a walkable approach square
  for (const p of [...DOOR_SLOTS, ...WILD_FLOWERS]) for (const [dx, dy] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) { const ch = g[p.ty + dy]?.[p.tx + dx]; if (ch === 'T' || ch === '~' || ch === 'r' || ch === '#') set(p.tx + dx, p.ty + dy, '.'); }
  // the start must be open
  set(START.tx, START.ty + 1, '=');
  return g.map(r => r.join(''));
}

export const WILDS_ROWS: string[] = buildRows();

export function isSolidId(id: number): boolean { return (SOLID_IDS as readonly number[]).includes(id); }
